import { useCallback, useMemo, useRef, useState } from 'react';

import { getBouncySumsConfig } from '@/features/bouncySums/helpers';
import { useBouncySumsGameFlow } from '@/features/bouncySums/hooks/useBouncySumsGameFlow';
import { useBouncySumsGameState } from '@/features/bouncySums/hooks/useBouncySumsGameState';
import { useBouncySumsLabels } from '@/features/bouncySums/hooks/useBouncySumsLabels';
import { useBouncySumsPhaserGame } from '@/features/bouncySums/hooks/useBouncySumsPhaserGame';
import { useBouncySumsSceneEvents } from '@/features/bouncySums/hooks/useBouncySumsSceneEvents';
import { GameAudioProvider, useResolvedGameAudioOn } from '@/shared/hooks/useGameAudioSettings';
import { IS_LOCAL_ENVIRONMENT } from '@/shared/config/env';

import BouncySumsHeader from '@/features/bouncySums/components/BouncySumsHeader';
import BouncySumsEndScreen from '@/features/bouncySums/components/BouncySumsEndScreen';
import BouncySumsLocalHud from '@/features/bouncySums/components/BouncySumsLocalHud';
import BouncySumsNumpad from '@/features/bouncySums/components/BouncySumsNumpad';
import BouncySumsStartScreen from '@/features/bouncySums/components/BouncySumsStartScreen';
import GameStyleBoundary from '@/shared/components/gameStyleBoundary/GameStyleBoundary';

import '@/features/bouncySums/styles/bouncy-sums.scss';

import type {
	BouncySumsGameData,
	BouncySumsQuestionsRequestEvent,
	BouncySumsSceneStateEvent,
} from '@/features/bouncySums/types';
import type {
	GameAnswerEvent,
	GameAudioProps,
	GameCloseEvent,
	GameData,
	GameFinishEvent,
	Question,
} from '@/features/gameSession/types';

type BouncySumsLevelProgress = {
	user_learning_path_section?: GameData | null;
};

export type BouncySumsGameProps = {
	gameData?: BouncySumsGameData | null;
	highScore?: number;
	levelProgress?: BouncySumsLevelProgress | null;
	questions?: Question[];
	onQuestionsRequest?: (event: BouncySumsQuestionsRequestEvent) => Promise<Question[]>;
	onAnswer?: (event: GameAnswerEvent) => void;
	onFinish?: (event: GameFinishEvent) => void;
	onClose: (event?: GameCloseEvent) => void;
	onReplay?: (gameData: GameData) => void;
	onTableSelection?: (gameData: GameData) => void;
} & GameAudioProps;

const BouncySumsGameContent = ({
	gameData: providedGameData,
	highScore,
	levelProgress,
	questions: providedQuestions,
	onQuestionsRequest,
	onAnswer,
	onFinish,
	onClose,
	onReplay,
	onTableSelection,
}: BouncySumsGameProps) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const audioOn = useResolvedGameAudioOn();
	const [sceneState, setSceneState] = useState<BouncySumsSceneStateEvent | null>(null);
	const labels = useBouncySumsLabels();

	const gameData = providedGameData ?? null;
	const questions = providedQuestions ?? [];
	const config = useMemo(() => getBouncySumsConfig(gameData, labels, highScore), [gameData, highScore, labels]);
	const {
		activeScreen,
		bouncyQuestions,
		handleBack,
		handleNumpadDigit,
		handleReplayGame,
		handleStartGame,
		handleTableToggle,
		isLoadingQuestions,
		returnToStartScreen,
		selectedTables,
		startGameTimeoutRef,
	} = useBouncySumsGameFlow({
		audioOn,
		config,
		onClose,
		onQuestionsRequest,
		questions,
		sceneState,
	});
	const gamePayload = useMemo(
		() => ({
			config,
			questions: bouncyQuestions,
		}),
		[bouncyQuestions, config],
	);

	useBouncySumsSceneEvents({
		onAnswer,
		onClose,
		onFinish,
		onStateChange: setSceneState,
		topicTitle: labels.topicTitle,
	});

	useBouncySumsPhaserGame({
		activeScreen,
		audioOn,
		containerRef,
		gamePayload,
		startGameTimeoutRef,
	});
	const gameState = useBouncySumsGameState({
		activeScreen,
		config,
		sceneState,
		selectedTables,
	});
	const nextGameData = levelProgress?.user_learning_path_section;
	const handleReplay = useCallback(() => {
		if (onReplay && nextGameData) {
			onReplay(nextGameData);
			handleReplayGame(nextGameData.questions);
			return;
		}

		handleReplayGame();
	}, [handleReplayGame, nextGameData, onReplay]);

	const handleTableSelection = useCallback(() => {
		if (onTableSelection) {
			if (nextGameData) {
				onTableSelection(nextGameData);
				returnToStartScreen();
			}
			return;
		}

		returnToStartScreen();
	}, [nextGameData, onTableSelection, returnToStartScreen]);

	return (
		<div className="bouncy-sums-game" data-screen={activeScreen} data-status={sceneState?.status ?? 'loading'}>
			<div className="bouncy-sums-game__screens">
				<BouncySumsStartScreen
					availableTables={config.tables}
					isLoadingQuestions={isLoadingQuestions}
					labels={labels}
					selectedTables={selectedTables}
					onStartGame={handleStartGame}
					onTableToggle={handleTableToggle}
				/>
				<div className="bouncy-sums-game__play-screen">
					<div ref={containerRef} className="bouncy-sums-game__phaser" />
					{gameState.isEndScreenVisible ? (
						<BouncySumsEndScreen
							answeredCount={sceneState?.answeredCount ?? 0}
							isHighScore={sceneState?.isHighScore ?? false}
							labels={labels}
							score={gameState.score}
							selectedTables={gameState.endScreenTables}
							onReplay={handleReplay}
							onTableSelection={handleTableSelection}
						/>
					) : null}
				</div>
			</div>
			<BouncySumsHeader
				audioToggleTooltip={labels.audioToggleTooltip}
				isGameScreen={gameState.isGameScreen}
				progress={gameState.progress}
				onBack={handleBack}
			/>
			<BouncySumsNumpad isVisible={gameState.isGameplayInputVisible} onDigit={handleNumpadDigit} />
			{IS_LOCAL_ENVIRONMENT ? (
				<BouncySumsLocalHud
					bpm={gameState.bpm}
					isGameScreen={gameState.isGameScreen}
					labels={labels}
					previousHighScore={gameState.previousHighScore}
					score={gameState.score}
					seconds={gameState.seconds}
				/>
			) : null}
		</div>
	);
};

const BouncySumsGame = ({ audioOn, onAudioChange, ...props }: BouncySumsGameProps) => {
	return (
		<GameStyleBoundary>
			<GameAudioProvider audioOn={audioOn} onAudioChange={onAudioChange}>
				<BouncySumsGameContent {...props} />
			</GameAudioProvider>
		</GameStyleBoundary>
	);
};

export default BouncySumsGame;
